/**
 * The Guardian Score waffle (DESIGN.md "Guardian Score waffle"): one 14px
 * square per point on a 10-wide grid with 2px gaps, grouped by component in
 * order. Each component's earned points fill with its category mark; the
 * rest are a 1px pt-off ring. The key lists each component with its swatch,
 * name and "got / max". A component with no data (`earned: null`) stays an
 * empty group and reads "No data" (§7 C7). Use largestRemainder() so the
 * earned squares add up to the score.
 *
 * The Command Center's one load moment: earned squares fill in order, 240ms
 * each, 8ms apart (off with `animate={false}`; prefers-reduced-motion zeroes
 * the durations in index.css).
 */
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { TONE_BG } from './tones'
import type { Category } from './types'

export interface WaffleSegment {
  category: Category
  /** Points earned, or null when the component has no data yet. */
  earned: number | null
  /** The component's share of the 100 points. */
  total: number
  label: string
}

export interface ScoreWaffleProps {
  segments: WaffleSegment[]
  animate?: boolean
  /** Accessible name of the grid. Default: a per-component summary. */
  label?: string
  className?: string
}

export function ScoreWaffle({ segments, animate = true, label, className }: ScoreWaffleProps) {
  const [lit, setLit] = useState(!animate)
  useEffect(() => {
    if (!animate) return
    const frame = requestAnimationFrame(() => setLit(true))
    return () => cancelAnimationFrame(frame)
  }, [animate])

  let order = 0
  const squares = segments.flatMap((seg) => {
    const size = Math.max(0, Math.round(seg.total))
    const on = seg.earned == null ? 0 : Math.min(size, Math.max(0, Math.round(seg.earned)))
    return Array.from({ length: size }, (_, i) => {
      const earned = i < on
      const n = earned ? order++ : 0
      return (
        <span
          key={`${seg.category}-${i}`}
          className={clsx(
            'h-3.5 w-3.5',
            earned && 'transition-[background-color,box-shadow] duration-240 ease-carbon-in',
            earned && lit ? TONE_BG[seg.category] : 'ring-1 ring-inset ring-cat-off',
          )}
          style={earned && animate ? { transitionDelay: `${n * 8}ms` } : undefined}
        />
      )
    })
  })
  const summary =
    label ??
    `Guardian Score breakdown: ${segments
      .map((s) => `${s.label} ${s.earned == null ? 'no data' : `${Math.round(s.earned)} of ${Math.round(s.total)}`}`)
      .join(', ')}. One square per point.`

  return (
    <div className={clsx('flex min-w-0 flex-col gap-4', className)}>
      <div role="img" aria-label={summary} className="grid w-max grid-cols-[repeat(10,14px)] gap-0.5">
        {squares}
      </div>
      <ul className="m-0 grid list-none gap-1.5 p-0">
        {segments.map((s) => (
          <li key={s.category + s.label} className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-2 text-helper text-text-secondary">
            <span aria-hidden="true" className={clsx('h-2.5 w-2.5', TONE_BG[s.category])} />
            <span className="min-w-0 truncate">{s.label}</span>
            <b className="font-semibold tabular-nums text-text-primary">
              {s.earned == null ? 'No data' : `${Math.round(s.earned)} / ${Math.round(s.total)}`}
            </b>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default ScoreWaffle
